import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { CustomerCodes } from "../../types";

export interface ImportResponse {
  successful_imports: number;
  failed_imports: number;
  errors?: string[];
}

export interface CustomerCodeMapping {
  mapping_id: string;
  customer_org_id: string;
  input_id: string;
  codes: CustomerCodes;
  description?: string;
  creator?: string;
  created?: string;
  modified?: string;
  modifier?: string;
}

export const customerCodeMappingApi = createApi({
  reducerPath: "customerCodeMappingApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/is/customer_code_mapping",
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    getCustomerCodeMappings: build.query<CustomerCodeMapping[], { 
      customer_org_id?: string; 
      input_id?: string; 
    }>({
      query: (params) => ({
        url: "",
        params,
      }),
    }),
    getCustomerCodeMapping: build.query<CustomerCodeMapping, string>({
      query: (id) => `/${id}`,
    }),
    createCustomerCodeMapping: build.mutation<CustomerCodeMapping, Partial<CustomerCodeMapping>>({
      query(body) {
        return {
          url: "",
          method: "POST",
          body,
        };
      },
    }),
    updateCustomerCodeMapping: build.mutation<CustomerCodeMapping, { 
      id: string; 
      body: Partial<CustomerCodeMapping> 
    }>({
      query({ id, body }) {
        return {
          url: `/${id}`,
          method: "PUT",
          body,
        };
      },
    }),
    deleteCustomerCodeMapping: build.mutation<void, string>({
      query(id) {
        return {
          url: `/${id}`,
          method: "DELETE",
        };
      },
    }),
    importCustomerCodeMappings: build.mutation<ImportResponse, FormData>({
      query(formData) {
        return {
          url: "/import",
          method: "POST",
          body: formData,
        };
      },
    }),
    getMappingsForInput: build.query<CustomerCodeMapping[], string>({
      query: (inputId) => `/input/${inputId}`,
    }),
    getMappingsForCustomer: build.query<CustomerCodeMapping[], string>({
      query: (customerOrgId) => `/customer/${customerOrgId}`,
    }),
  }),
});

export const {
  useGetCustomerCodeMappingsQuery,
  useGetCustomerCodeMappingQuery,
  useCreateCustomerCodeMappingMutation,
  useUpdateCustomerCodeMappingMutation,
  useDeleteCustomerCodeMappingMutation,
  useGetMappingsForInputQuery,
  useGetMappingsForCustomerQuery,
  useImportCustomerCodeMappingsMutation,
} = customerCodeMappingApi;
